

import { AddPrimitivesCommand, BasicControl, Circle, DYMODE_MODE, ExtrudeSolidT, Line, Listeners, MOUSE, Plane, Point, Polygon, TnEngineContext, TnEngineExtraContext, ViewEditor, dymode } from 'pytha';
import { Vector3 } from 'three';
import { ARCHI_CTRL_TYPE } from '../enums/archibase-control-type';
import { findLayersInProjectUuid } from '@/api/geometry/layer';
import { getSpecialPrimitiveLayerUuid } from '@/global/utils/layer-utils';
import { LayerType } from '@/enums/layer-type';

const STEP = {
    1: { 'tip': '指定柱的基点', 'mode': DYMODE_MODE.POINT },
    2: { 'tip': '指定柱的高度', 'mode': DYMODE_MODE.DIST },
}

export class AssignColumnControl extends BasicControl {

    id = ARCHI_CTRL_TYPE.ASSIGN_COLUMN;

    point_down: Point = new Point();
    point_move: Point = new Point();

    dymodePoint: Vector3 = new Vector3();
    capturePoint: Vector3;
    orthogonalPoint: Vector3;
    polarPoint: Vector3;

    context: TnEngineContext;
    extraContext: TnEngineExtraContext;

    isInit: boolean = false

    step: number = 1

    type: string
    width: number
    length: number
    radius: number

    originPoint: Point = new Point()
    helpColumn: ExtrudeSolidT;

    projectId: string = ""

    constructor(editor: ViewEditor, listeners: Listeners) {
        super()
        this.needInit = true
        this.editor = editor;
        this.listeners = listeners;
        this.context = TnEngineContext.getInstance();
        this.extraContext = TnEngineExtraContext.getInstance();
        this.listeners.signals.needCapture.dispatch();

        editor.renderer.domElement.addEventListener('pointermove', this.onPointerMove);
        editor.renderer.domElement.addEventListener('pointerdown', this.onPointerDown);
        listeners.signals.onPointCapture.add(this.onPointCapture);
        listeners.signals.onPointOrthogonal.add(this.onPointOrthogonal);
        listeners.signals.onPointPolarTracking.add(this.onPointPolarTracking);
        listeners.signals.setInitControlData.add(this.initControl)
        listeners.signals.onDymodeChanged.add(this.onDymodeChanged);
        // 钝化选择
        this.editor.selectControl.setEnabled(false);
        // 激活动态输入
        this.extraContext.dymodeContext.enabled = true;

        this.listeners.signals.sendToDymodeTip.dispatch(STEP[this.step].tip + ':');
        this.extraContext.dymodeContext.setMode(STEP[this.step].mode);
        this.extraContext.stmodeContext.setStmode(STEP[this.step].tip);

        console.log("AssignColumnControl add");
    }

    dispose(): void {
        this.editor.renderer.domElement.removeEventListener('pointermove', this.onPointerMove);
        this.editor.renderer.domElement.removeEventListener('pointerdown', this.onPointerDown);
        this.listeners.signals.onPointCapture.remove(this.onPointCapture);
        this.listeners.signals.onPointOrthogonal.remove(this.onPointOrthogonal);
        this.listeners.signals.onPointPolarTracking.remove(this.onPointPolarTracking);
        this.listeners.signals.setInitControlData.remove(this.initControl)
        this.listeners.signals.onDymodeChanged.remove(this.onDymodeChanged);
        this.listeners.signals.noNeedCapture.dispatch();
        this.editor.selectControl.setEnabled(true);
        this.extraContext.dymodeContext.enabled = false;
        this.editor.clampControl.enabled = true;
        this.editor.orthogonalControl.enabled = false;
        this.editor.polarControl.enabled = false;

        let obj = this?.helpColumn?.viewObj;
        if (obj && !!obj.parent) {
            obj.parent.remove(obj);
        }

        console.log("AssignColumnControl removed");
    }

    onPointPolarTracking = (point) => {
        this.polarPoint = point;
    }

    onPointOrthogonal = (point) => {
        this.orthogonalPoint = point;
    }

    onPointCapture = (point) => {
        this.capturePoint = point;
    }

    initControl = (data: any) => {
        this.type = data.type
        this.width = Number(data.width)
        this.length = Number(data.length)
        this.radius = Number(data.radius)
        this.projectId = data.projectId
        this.isInit = true
    }

    private _projectPoint = new Point();
    private _axisZ = TnEngineContext.getInstance().ucsContext.getAxisList().axisZ;
    private _extrudePlane = new Plane();
    private _cameraDir = new Vector3();
    private _screenUp = new Vector3();
    heightVec: Vector3;
    height: number
    onPointerMove = () => {
        const x = this.extraContext.mousePosContext.x;
        const y = this.extraContext.mousePosContext.y;
        const z = this.extraContext.mousePosContext.z;
        this.point_move.set(x, y, z);
        if (this.capturePoint) {
            this.point_move.copy(this.capturePoint);
        } else if (this.orthogonalPoint) {
            this.point_move.copy(this.orthogonalPoint);
        } else if (this.polarPoint) {
            this.point_move.copy(this.polarPoint);
        }

        switch (this.step) {
            case 2:
                // 开始拉拽，计算高度
                let camera = this.editor.camera;
                camera.getWorldDirection(this._cameraDir);
                this._screenUp.copy(camera.up).cross(this._cameraDir).cross(this._cameraDir);
                // 获得光标所在平面，该平面垂直于屏幕，且水平
                this._extrudePlane.setFromNormalAndCoplanarPoint(this._screenUp, this.point_move);
                this.heightVec = this.point_move.clone().sub(this.originPoint);
                if (Math.abs(this._screenUp.dot(this._axisZ)) < 1e-6) {
                    this.height = this.heightVec.length();
                } else {
                    this._extrudePlane.intersectStraightLine(this.originPoint, this._axisZ, this._projectPoint);
                    this.height = this._projectPoint.sub(this.originPoint).length();
                    if (this._projectPoint.angleTo(this._axisZ) > Math.PI / 2) {
                        this.height *= -1;
                    }
                }
                this.helpColumn._geo.height = this.height
                this.helpColumn.updateViewGeometry()
                break;
            default:
                break;
        }
    }

    onDymodeChanged = (value: dymode) => {
        if (value.mode === DYMODE_MODE.POINT) {
            if (this.step == 1) {
                this.dymodePoint.set(value.x, value.y, value.z);
                this.point_down.copy(this.dymodePoint)
                this.step_1()
            }
        } else if (value.mode == DYMODE_MODE.DIST) {
            if (this.step == 2) {
                if (this.height < 0) {
                    this.height = value.dist
                    this.height *= -1
                } else {
                    this.height = value.dist
                }
                this.confirmColumn()
            }
        }
    }

    onPointerDown = (event) => {
        if (event.button === MOUSE.RIGHT || !this.isInit) {
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
            return;
        }
        if (event.button !== MOUSE.LEFT) return;
        this.point_down.set(this.extraContext.mousePosContext.x, this.extraContext.mousePosContext.y, this.extraContext.mousePosContext.z);
        if (this.capturePoint) {
            this.point_down.copy(this.capturePoint);
        } else if (this.orthogonalPoint) {
            this.point_down.copy(this.orthogonalPoint);
        } else if (this.polarPoint) {
            this.point_down.copy(this.polarPoint);
        }

        if (this.step == 1) {
            this.step_1()
        } else if (this.step == 2) {
            this.step_2()
        }
    }

    step_1 = () => {
        this.originPoint.copy(this.point_down)
        // 构建底面
        if (this.type == "circle") {
            let bottomFace = new Polygon([new Circle(this.originPoint, this.radius)])
            this.helpColumn = new ExtrudeSolidT(bottomFace, new Vector3(0, 0, 1), 0)
        } else {
            let point1 = new Point(-this.width / 2, this.length / 2, 0).add(this.originPoint)
            let point2 = new Point(this.width / 2, this.length / 2, 0).add(this.originPoint)
            let point3 = new Point(-this.width / 2, -this.length / 2, 0).add(this.originPoint)
            let point4 = new Point(this.width / 2, -this.length / 2, 0).add(this.originPoint)
            let bottomFace = new Polygon([new Line(point1, point3), new Line(point3, point4), new Line(point4, point2), new Line(point2, point1)])
            this.helpColumn = new ExtrudeSolidT(bottomFace, new Vector3(0, 0, 1), 0)
        }
        this.extraContext.sceneContext.scene.add(this.helpColumn.render())

        this.step++
        this.listeners.signals.sendToDymodeTip.dispatch(STEP[this.step].tip + ':');
        this.extraContext.dymodeContext.setMode(STEP[this.step].mode);
        this.extraContext.stmodeContext.setStmode(STEP[this.step].tip);
        this.listeners.signals.orthogonal.dispatch(this.point_down);
        this.listeners.signals.polarTracking.dispatch(this.point_down);
        this.extraContext.dymodeContext.setMode(DYMODE_MODE.DIST, this.point_down);
    }

    step_2 = () => {
        // 重新计算高度
        let camera = this.editor.camera;
        camera.getWorldDirection(this._cameraDir);
        this._screenUp.copy(camera.up).cross(this._cameraDir).cross(this._cameraDir);
        // 获得光标所在平面，该平面垂直于屏幕，且水平
        this._extrudePlane.setFromNormalAndCoplanarPoint(this._screenUp, this.point_down);
        this.heightVec = this.point_down.clone().sub(this.originPoint);
        if (Math.abs(this._screenUp.dot(this._axisZ)) < 1e-6) {
            this.height = this.heightVec.length();
        } else {
            this._extrudePlane.intersectStraightLine(this.originPoint, this._axisZ, this._projectPoint);
            this.height = this._projectPoint.sub(this.originPoint).length();
            if (this._projectPoint.angleTo(this._axisZ) > Math.PI / 2) {
                this.height *= -1;
            }
        }
        // 确认高度，完成布置
        this.confirmColumn()
    }

    confirmColumn = () => {
        getSpecialPrimitiveLayerUuid(this.projectId, LayerType.COLUMN).then((layerUuid: string) => {
            this.helpColumn._geo.height = this.height
            this.helpColumn.layerUuid = layerUuid
            let command = new AddPrimitivesCommand([this.helpColumn.clone()])
            this.editor.history.execute(command);
            this.listeners.signals.onOpeCommandControlEnd.dispatch();
        })
    }

}